import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import rafSchedule from 'raf-schd';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { findOverflowScrollParent } from '@atlaskit/editor-common/Popup/utils';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { Card as SmartCard } from '@atlaskit/smart-card/card/lazy';
import type { OnClickCallback } from '@atlaskit/smart-card/card/types';
import { CardSSR } from '@atlaskit/smart-card/ssr';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

import { registerCard, removeCard } from '../pm-plugins/actions';
import { visitCardLinkAnalytics } from '../ui/toolbar';
import type { SmartCardProps } from './genericCard';
import { isIntersectionObserverSupported } from './isIntersectionObserverSupported';

export const InlineCard: React.MemoExoticComponent<
	({
		node,
		cardContext,
		actionOptions,
		useAlternativePreloader,
		view,
		getPos,
		onClick,
		onResolve,
		isHovered,
		showHoverPreview,
		hoverPreviewOptions,
		isPageSSRed,
		pluginInjectionApi,
		disablePreviewPanel,
	}: SmartCardProps) => React.JSX.Element | null
> = memo(
	({
		node,
		cardContext,
		actionOptions,
		useAlternativePreloader,
		view,
		getPos,
		onClick: propsOnClick,
		onResolve: onRes,
		isHovered,
		showHoverPreview,
		hoverPreviewOptions,
		isPageSSRed,
		pluginInjectionApi,
		disablePreviewPanel,
	}: SmartCardProps): React.JSX.Element | null => {
		const { url, data } = node.attrs;
		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		const refId = useRef(uuid());
		const removeCardDispatched = useRef(false);
		const pendingRegisterCard = useRef<ReturnType<typeof rafSchedule> | null>(null);

		useEffect(() => {
			return () => {
				if (isExperimentEnabled('platform_editor_reduce_memory_leaks')) {
					pendingRegisterCard.current?.cancel();
					pendingRegisterCard.current = null;
				}
			};
		}, []);

		const cardActions = pluginInjectionApi?.card.actions;
		const registerResolvedCard = cardActions?.registerCard;
		const removeRegisteredCard = cardActions?.removeRegisteredCard;

		const { getState: getSmartlinkState } = cardContext?.value?.store || {};
		const cardState = getSmartlinkState?.()[url || ''];

		useEffect(() => {
			const id = refId.current;
			removeCardDispatched.current = false;
			return () => {
				if (
					isExperimentEnabled('platform_editor_inline_card_dispatch_guard') &&
					removeCardDispatched.current
				) {
					return;
				}
				removeCardDispatched.current = true;
				if (isExperimentEnabled('platform_editor_reduce_forced_layout_2')) {
					removeRegisteredCard?.(id);
				} else {
					const { tr } = view.state;
					removeCard({ id })(tr);
					view.dispatch(tr);
				}
			};
		}, [
			getPos,
			isExperimentEnabled('platform_editor_reduce_forced_layout_2') ? undefined : view,
			isExperimentEnabled('platform_editor_reduce_forced_layout_2')
				? removeRegisteredCard
				: undefined,
		]);

		const scrollContainer: HTMLElement | undefined = useMemo(
			() =>
				isExperimentEnabled('platform_editor_reduce_forced_layout_2') &&
				isIntersectionObserverSupported()
					? undefined
					: // eslint-disable-next-line @atlaskit/editor/no-as-casting
						findOverflowScrollParent(view.dom as HTMLElement) || undefined,
			[view.dom],
		);

		const onResolve = useCallback(
			(data: { title?: string; url?: string }) => {
				if (!getPos || typeof getPos === 'boolean') {
					return;
				}

				const { title, url } = data;
				// don't dispatch immediately since we might be in the middle of
				// rendering a nodeview
				const scheduleRegisterCard = rafSchedule(() => {
					if (isExperimentEnabled('platform_editor_reduce_forced_layout_2')) {
						if (removeCardDispatched.current || getPos() === undefined) {
							return;
						}
						const id = refId.current;
						registerResolvedCard?.({ id, title, url, getPos });
						onRes?.(undefined, title);
					} else {
						// prosemirror-bump-fix
						const pos = getPos();

						if (typeof pos !== 'number') {
							return;
						}

						const tr = view.state.tr;

						registerCard({
							title,
							url,
							pos,
							id: refId.current,
						})(tr);

						onRes?.(tr, title);

						view.dispatch(tr);
					}
				});
				if (isExperimentEnabled('platform_editor_reduce_memory_leaks')) {
					pendingRegisterCard.current = scheduleRegisterCard;
				}
				scheduleRegisterCard();
			},
			[
				getPos,
				isExperimentEnabled('platform_editor_reduce_forced_layout_2') ? undefined : view,
				onRes,
				isExperimentEnabled('platform_editor_reduce_forced_layout_2')
					? registerResolvedCard
					: undefined,
			],
		);

		const onError = useCallback(
			(data: { err?: Error; url?: string }) => {
				const { url, err } = data;
				if (err) {
					throw err;
				}
				onResolve({ url });
			},
			[onResolve],
		);

		const handleOnClick: OnClickCallback = useCallback(
			(event, data) => {
				if (event.metaKey || event.ctrlKey) {
					const { actions: editorAnalyticsApi } = pluginInjectionApi?.analytics ?? {};

					visitCardLinkAnalytics(editorAnalyticsApi, INPUT_METHOD.META_CLICK)(
						view.state,
						view.dispatch,
					);

					window.open(data?.destinationUrl ?? url, '_blank');
				} else {
					// only trigger the provided onClick callback if the meta key or ctrl key is not pressed
					propsOnClick?.(event);
				}
			},
			[propsOnClick, url, view, pluginInjectionApi],
		);

		const onClick = editorExperiment('platform_editor_controls', 'variant1')
			? handleOnClick
			: propsOnClick;

		const card = useMemo(() => {
			if ((isPageSSRed || cardState) && url) {
				return (
					<CardSSR
						key={url}
						url={url}
						appearance="inline"
						onClick={onClick}
						container={scrollContainer}
						onResolve={onResolve}
						onError={onError}
						inlinePreloaderStyle={useAlternativePreloader ? 'on-right-without-skeleton' : undefined}
						actionOptions={actionOptions}
						isHovered={isHovered}
						showHoverPreview={showHoverPreview}
						hoverPreviewOptions={hoverPreviewOptions}
						disablePreviewPanel={disablePreviewPanel}
						hideIconLoadingSkeleton
					/>
				);
			}

			return (
				<SmartCard
					key={url}
					url={url ?? data.url}
					appearance="inline"
					onClick={onClick}
					container={scrollContainer}
					onResolve={onResolve}
					onError={onError}
					inlinePreloaderStyle={useAlternativePreloader ? 'on-right-without-skeleton' : undefined}
					actionOptions={actionOptions}
					isHovered={isHovered}
					showHoverPreview={showHoverPreview}
					hoverPreviewOptions={hoverPreviewOptions}
					disablePreviewPanel={disablePreviewPanel}
				/>
			);
		}, [
			url,
			data,
			onClick,
			scrollContainer,
			onResolve,
			onError,
			useAlternativePreloader,
			actionOptions,
			isHovered,
			showHoverPreview,
			hoverPreviewOptions,
			isPageSSRed,
			disablePreviewPanel,
			cardState,
		]);

		// [WS-2307]: we only render card wrapped into a Provider when the value is ready,
		// otherwise if we got data, we can render the card directly since it doesn't need the Provider
		return cardContext && cardContext.value ? (
			<cardContext.Provider value={cardContext.value}>{card}</cardContext.Provider>
		) : data ? (
			card
		) : null;
	},
);
